import React, { useState } from 'react';
import { formatValue } from './DataPointsTable';
import { textToHtml } from './SummaryEditor';
import Highlight from './Highlight';
import {
  accountNote, asOfOf, completionOf, fieldRowsOf, institutionOf, isSharedField, matchKeyOf, rowAnnotations, startsWithPath,
  typedMatches, valueEntriesOf, titleOf,
} from '../utils/clientReport';
import { fieldLabel } from '../utils/reportSearch';
import { formatTimestamp, formatWhen } from '../utils/dateUtils';
import { personName } from '../utils/people';

export { fieldLabel };

const isPlainObject = (value) => value && typeof value === 'object' && !Array.isArray(value);

// The Build prompts write "Unknown" (not provided) or "Unclear" (ambiguous);
// flag those so the paraplanner sees the gaps.
const GAP_VALUES = { unknown: 'Not provided in the document', unclear: 'Present but unclear in the document' };

const isEmpty = (value) => value === null || value === undefined || value === '';
const isSingleValue = (value) => !Array.isArray(value) && !isPlainObject(value);

const NO_ANNOTATIONS = { corrections: [], sync: [], notes: [] };
const RERUN_CONFLICT = 'This document was re-run while you were editing. Check the value, then save again.';

const shown = (value) => (isEmpty(value) ? '(empty)' : formatValue(value));

const isNestedTable = (value) => Array.isArray(value) && value.length > 0 && value.every(isPlainObject);

/**
 * A list of objects (e.g. Holdings) as a table. With eMoney marks shown, each
 * row gets one checkbox for all of its values (a holding is entered in eMoney
 * as one line); every value still keeps its own mark underneath.
 */
const NestedTable = ({ rows, path = [], tokens, edit }) => {
  const columns = [];
  rows.forEach((row) => Object.keys(row).forEach((key) => { if (!columns.includes(key)) columns.push(key); }));
  const withChecks = Boolean(edit && edit.showSync);
  const tableLabel = fieldLabel(path.filter((part) => typeof part === 'string').slice(-1)[0] || '');
  return (
    <table className="table table-sm table-bordered mb-0 small">
      <thead>
        <tr>
          {columns.map((col) => <th key={col} className="bg-light fw-normal text-muted">{fieldLabel(col)}</th>)}
          {withChecks && <th className="bg-light" style={{ width: 32 }}><span className="visually-hidden">In eMoney</span></th>}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, idx) => {
          const rowPath = [...path, idx];
          const scope = `row:${JSON.stringify(rowPath)}`;
          return (
            <tr key={idx}>
              {columns.map((col) => (
                <td key={col}>
                  <NestedValue value={row[col]} path={[...rowPath, col]} tokens={tokens} edit={edit} />
                </td>
              ))}
              {withChecks && (
                <td className="text-center align-middle">
                  <GroupCheck
                    completion={edit.completionUnder(rowPath, (entry) => entry.rowKey)}
                    label={`${tableLabel} row ${idx + 1}`}
                    canEdit={edit.canEdit}
                    busy={edit.isBulkBusy(scope)}
                    disabled={edit.docBulkBusy}
                    onToggle={(synced) => edit.syncUnder(scope, rowPath, synced)}
                  />
                </td>
              )}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

/**
 * The entered-in-eMoney check for a group of values -- a nested table or one
 * of its rows -- drawn like a single value's SyncMark: green when every value
 * is marked and unchanged, a dash when only some are, amber when a value
 * changed after it was checked. Viewers see the icon only.
 */
const GroupCheck = ({ completion, label, canEdit, busy, disabled, onToggle, unit = 'values' }) => {
  const { done, total, stale } = completion;
  const all = total > 0 && done === total;
  const some = !all && (done > 0 || stale > 0);
  if (!total) return null;
  const state = all ? 'done' : stale ? 'stale' : some ? 'partial' : 'none';
  const icon = {
    done: 'bi-check-square-fill text-success',
    stale: 'bi-exclamation-square-fill text-warning',
    partial: 'bi-dash-square text-success',
    none: 'bi-square text-muted',
  }[state];
  let title = all ? 'Entered in eMoney' : `${done} of ${total} ${unit} entered in eMoney`;
  if (stale) title += ` · ${stale} changed after ${stale === 1 ? 'it was' : 'they were'} checked`;
  if (busy) {
    return <span className="spinner-border spinner-border-sm text-secondary" role="status" aria-label={`Saving ${label}`} data-testid="group-saving" />;
  }
  if (!canEdit) {
    if (!done && !stale) return null;
    return <i className={`bi ${icon}`} title={title} aria-label={`${label}: ${title}`} data-testid="group-mark" data-state={state} />;
  }
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={all ? 'true' : some ? 'mixed' : 'false'}
      className="btn btn-link btn-sm p-0 border-0"
      onClick={() => onToggle(!all)}
      disabled={disabled}
      title={title}
      aria-label={`${label} entered in eMoney`}
      data-testid="group-check"
      data-state={state}
    >
      <i className={`bi ${icon}`}></i>
    </button>
  );
};

function formatCell(value, { path, tokens, edit } = {}) {
  if (Array.isArray(value)) {
    if (isNestedTable(value)) return <NestedTable rows={value} path={path} tokens={tokens} edit={edit} />;
    return value.filter((v) => v !== null && v !== undefined).map(formatValue).join(', ');
  }
  if (isPlainObject(value)) {
    return Object.entries(value)
      .filter(([, v]) => v !== null && v !== undefined)
      .map(([k, v]) => `${fieldLabel(k)}: ${Array.isArray(v) || isPlainObject(v) ? JSON.stringify(v) : formatValue(v)}`)
      .join(' · ');
  }
  if (typeof value === 'string' && GAP_VALUES[value.trim().toLowerCase()]) {
    return (
      <span className="text-warning-emphasis fst-italic" title={GAP_VALUES[value.trim().toLowerCase()]} data-testid="gap-value">
        {value}
      </span>
    );
  }
  return formatValue(value);
}

/** A cell's value, with search words marked when it is plain text. */
const Cell = ({ value, tokens, path, edit }) => {
  const cell = formatCell(value, { path, tokens, edit });
  return typeof cell === 'string' ? <Highlight text={cell} tokens={tokens} /> : cell;
};

const NestedValue = ({ value, path, tokens, edit }) => {
  if (Array.isArray(value)) {
    if (value.length && value.every(isPlainObject)) {
      return <NestedTable rows={value} path={path} tokens={tokens} edit={edit} />;
    }
    return <Cell value={value} tokens={tokens} />;
  }
  if (isPlainObject(value)) {
    return (
      <span className="d-inline-flex flex-wrap gap-1">
        {Object.entries(value).map(([key, child], index) => (
          <span key={key}>{index > 0 && ' · '}{fieldLabel(key)}: <NestedValue value={child} path={[...path, key]} tokens={tokens} edit={edit} /></span>
        ))}
      </span>
    );
  }
  const rowKey = JSON.stringify(path);
  const row = edit.rowByKey(rowKey);
  const target = row || { path };
  const correction = edit.lookup.correction(target);
  const label = path.map((part) => (typeof part === 'number' ? `Row ${part + 1}` : fieldLabel(part))).join(' › ');
  const busy = edit.busyKey === rowKey || edit.docBulkBusy;
  return (
    <span className="d-inline-flex flex-wrap align-items-center gap-1">
      {edit.editingKey === rowKey ? (
        <form
          className="d-flex flex-wrap gap-1 align-items-center d-print-none"
          onSubmit={(event) => { event.preventDefault(); edit.saveEdit(); }}
        >
          <input
            className="form-control form-control-sm"
            style={{ maxWidth: 180 }}
            value={edit.draft}
            onChange={(event) => edit.setDraft(event.target.value)}
            aria-label={`Corrected ${label}`}
            autoFocus
          />
          <button type="submit" className="btn btn-sm btn-primary" disabled={busy}>Save</button>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={edit.cancelEdit} disabled={busy}>Cancel</button>
          {edit.conflictNote && <span className="text-warning-emphasis small w-100" data-testid="edit-conflict">{edit.conflictNote}</span>}
        </form>
      ) : (
        <>
          <Cell value={value === null || value === undefined ? '' : value} tokens={tokens} />
          {correction && (
            <span className="badge bg-info-subtle text-info-emphasis fw-normal" data-testid="edited-badge">
              edited
            </span>
          )}
          {edit.editing && (
            <span className="d-print-none d-inline-flex gap-1">
              <button type="button" className="btn btn-link btn-sm p-0" onClick={() => edit.startEdit(rowKey, value)} aria-label={`Edit ${label}`} title="Correct this value">
                <i className="bi bi-pencil"></i>
              </button>
              {correction && (
                <button type="button" className="btn btn-link btn-sm p-0 text-muted" onClick={() => edit.revert(rowKey)} disabled={busy} aria-label={`Revert ${label}`} title="Go back to the extracted value">
                  <i className="bi bi-arrow-counterclockwise"></i>
                </button>
              )}
            </span>
          )}
        </>
      )}
    </span>
  );
};

/**
 * The entered-in-eMoney mark for one value: who checked it off and when, or
 * a warning when the value changed afterwards. Editors can check or uncheck.
 */
const SyncMark = ({ mark, label, canEdit, busy, onToggle, who }) => {
  const stale = mark && mark.stale;
  const synced = mark && !mark.stale;
  let title = `Not entered in eMoney yet${canEdit ? ' — click once you have' : ''}`;
  if (synced) title = `Entered in eMoney by ${who(mark.by)} on ${formatWhen(mark.at)}`;
  if (stale) title = `Changed after ${who(mark.by)} entered it in eMoney on ${formatWhen(mark.at)} (was ${mark.value || '(empty)'}). Check it again once eMoney is updated.`;
  let icon = 'bi-square text-muted';
  if (synced) icon = 'bi-check-square-fill text-success';
  if (stale) icon = 'bi-exclamation-square-fill text-warning';
  if (!canEdit) {
    if (!mark) return null;
    return <i className={`bi ${icon}`} title={title} data-testid="sync-mark" data-state={stale ? 'stale' : 'synced'} aria-label={title}></i>;
  }
  return (
    <button
      type="button"
      className="btn btn-link btn-sm p-0 border-0"
      onClick={onToggle}
      disabled={busy}
      title={title}
      aria-label={`${label}: ${title}`}
      data-testid="sync-mark"
      data-state={stale ? 'stale' : (synced ? 'synced' : 'none')}
    >
      <i className={`bi ${icon}`}></i>
    </button>
  );
};

/** One value row: label, value (editable for editors in edit mode), and its eMoney mark. */
const ValueRow = ({
  label, title, value, path, nested, tokens, correction, mark, edit, rowKey,
}) => {
  const {
    editing, canEdit, showSync, busyKey, editingKey, draft, setDraft, startEdit, cancelEdit, saveEdit, revert, toggleSync, sharedNote,
    conflictNote,
  } = edit;
  const isEditingThis = editingKey === rowKey;
  const busy = busyKey === rowKey;
  const editable = editing && isSingleValue(value);
  return (
    <tr data-testid="value-row" data-field={rowKey}>
      <th scope="row" className={`small fw-normal text-muted${nested ? ' ps-3' : ''}`} style={{ width: '40%' }} title={title}>
        <Highlight text={label} tokens={tokens} />
      </th>
      <td className="small">
        {isEditingThis ? (
          <form
            className="d-flex flex-wrap gap-1 align-items-center d-print-none"
            onSubmit={(e) => { e.preventDefault(); saveEdit(); }}
          >
            <input
              className="form-control form-control-sm"
              style={{ maxWidth: 260 }}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              aria-label={`Corrected ${label}`}
              autoFocus
            />
            <button type="submit" className="btn btn-sm btn-primary" disabled={busy}>Save</button>
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={cancelEdit} disabled={busy}>Cancel</button>
            {sharedNote && <span className="text-muted small">{sharedNote}</span>}
            {conflictNote && (
              <span className="text-warning-emphasis small w-100" data-testid="edit-conflict">{conflictNote}</span>
            )}
          </form>
        ) : (
          <span className="d-inline-flex flex-wrap align-items-center gap-1">
            <Cell value={value} tokens={tokens} path={path} edit={edit} />
            {correction && (
              <span
                className="badge bg-info-subtle text-info-emphasis fw-normal"
                title={`Extracted value: ${shown(correction.original)}. Corrected by ${edit.who(correction.by)} on ${formatWhen(correction.at)}.`}
                data-testid="edited-badge"
              >
                edited
              </span>
            )}
            {editable && (
              <span className="d-print-none d-inline-flex gap-1">
                <button type="button" className="btn btn-link btn-sm p-0" onClick={() => startEdit(rowKey, value)} aria-label={`Edit ${label}`} title="Correct this value">
                  <i className="bi bi-pencil"></i>
                </button>
                {correction && (
                  <button type="button" className="btn btn-link btn-sm p-0 text-muted" onClick={() => revert(rowKey)} disabled={busy} aria-label={`Revert ${label}`} title={`Go back to the extracted value (${shown(correction.original)})`}>
                    <i className="bi bi-arrow-counterclockwise"></i>
                  </button>
                )}
              </span>
            )}
          </span>
        )}
      </td>
      {showSync && (
        <td className="small text-center" style={{ width: 36 }}>
          {isSingleValue(value) && (
            <SyncMark mark={mark} label={label} canEdit={canEdit} busy={busy || edit.docBulkBusy} onToggle={() => toggleSync(rowKey, value)} who={edit.who} />
          )}
          {isNestedTable(value) && (
            <GroupCheck
              completion={edit.completionUnder(path)}
              label={label}
              unit="rows"
              canEdit={canEdit}
              busy={edit.isBulkBusy(`table:${JSON.stringify(path)}`)}
              disabled={edit.docBulkBusy}
              onToggle={(synced) => edit.syncUnder(`table:${JSON.stringify(path)}`, path, synced)}
            />
          )}
        </td>
      )}
    </tr>
  );
};

/**
 * The account's field rows; a nested object becomes a sub-heading followed
 * by its own fields. rows: [{ rowKey, key, childKey }] from fieldRowsOf.
 */
const FieldRows = ({ fields, rows, tokens, edit, lookup }) => rows.map((row) => {
  if (row.group) {
    return (
      <tr key={row.rowKey}>
        <th colSpan={edit.showSync ? 3 : 2} className="small bg-light" data-testid="field-group">
          <Highlight text={fieldLabel(row.key)} tokens={tokens} />
        </th>
      </tr>
    );
  }
  const nested = row.childKey !== undefined;
  const value = nested ? fields[row.key][row.childKey] : fields[row.key];
  const path = [row.key, ...(nested ? [row.childKey] : [])];
  return (
    <ValueRow
      key={row.rowKey}
      rowKey={row.rowKey}
      label={fieldLabel(nested ? row.childKey : row.key)}
      title={nested ? `${row.key} › ${row.childKey}` : row.key}
      value={value}
      path={path}
      nested={nested}
      tokens={tokens}
      correction={lookup.correction(row)}
      mark={lookup.mark(row)}
      edit={edit}
    />
  );
});

const SourceLinks = ({ doc }) => (
  <>
    {doc.url && (
      <a href={doc.url} target="_blank" rel="noopener noreferrer" className="d-print-none">Open file</a>
    )}
    {doc.sharepointUrl && (
      <a href={doc.sharepointUrl} target="_blank" rel="noopener noreferrer" className="d-print-none">Open in SharePoint</a>
    )}
  </>
);

/**
 * Re-runs one document's extraction (its section's Build prompt only) so a
 * prompt edit can be checked. state: undefined | { state: 'running' | 'done'
 * | 'failed', message }.
 */
export const RerunButton = ({ fileId, state, onRerun }) => {
  if (!onRerun) return null;
  const running = state && state.state === 'running';
  return (
    <span className="d-print-none d-inline-flex align-items-center gap-2">
      <button
        type="button"
        className="btn btn-link btn-sm p-0"
        onClick={() => onRerun(fileId)}
        disabled={running}
        title="Run this document's extraction prompt again (one model call). Use after editing the prompt in Prompt Manager."
      >
        {running ? (
          <>
            <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
            Re-running…
          </>
        ) : (
          <>
            <i className="bi bi-arrow-repeat me-1"></i>Re-run extraction
          </>
        )}
      </button>
      {state && state.state === 'done' && <span className="text-success" data-testid="rerun-done">{state.message || 'Updated'}</span>}
      {state && state.state === 'failed' && <span className="text-danger" data-testid="rerun-failed">{state.message}</span>}
    </span>
  );
};

const HISTORY_ACTIONS = {
  correction: { set: 'corrected', revert: 'reverted' },
  sync: { check: 'checked off', uncheck: 'unchecked' },
  note: { set: 'noted', clear: 'cleared the note' },
};

const historyField = (entry) => {
  if (entry.kind === 'note') return '';
  const field = Array.isArray(entry.field) ? entry.field.filter((s) => typeof s === 'string') : [];
  return field.map(fieldLabel).join(' › ');
};

/** Who corrected, checked off or noted what on the document, newest first. */
const HistoryList = ({ state, who }) => {
  if (state.loading) return <div className="small text-muted px-2 py-1 border-top d-print-none">Loading history…</div>;
  if (state.error) return <div className="small text-danger px-2 py-1 border-top d-print-none">{state.error}</div>;
  return (
    <ul className="list-unstyled small border-top px-2 py-1 mb-0 d-print-none" data-testid="account-history">
      {state.entries.length === 0 && <li className="text-muted">No changes yet.</li>}
      {state.entries.map((entry, idx) => (
        <li key={idx}>
          <span className="text-muted">{formatWhen(entry.at)}</span>{' '}
          <strong>{who(entry.by)}</strong>{' '}
          {(HISTORY_ACTIONS[entry.kind] || {})[entry.action] || entry.action}{' '}
          {historyField(entry)}
          {entry.kind === 'correction' && <> {shown(entry.old)} → {shown(entry.new)}</>}
          {entry.kind === 'note' && entry.new && <> “{entry.new}”</>}
        </li>
      ))}
    </ul>
  );
};

/**
 * AccountTable
 *
 * One account in the client report: eMoney field on the left, value on the
 * right, in the order the extraction returned them, with the source file and
 * the prompt that produced it underneath. Older statements for the same
 * account are listed collapsed. tokens: search words to mark; domId: the
 * element id the report's search jumps to.
 *
 * Corrections, entered-in-eMoney marks and the account's note come from
 * doc.annotations (the values in `fields` are already corrected).
 * canEdit allows changing them through onAnnotate(fileId, kind, payload),
 * kind 'correction' | 'sync' | 'note', which resolves to an error message
 * or null; onLoadHistory(fileId) resolves to the document's history.
 */
// onBulkSync(scope, [{ doc, entry }], synced): mark many values at once (the
// report sends one save per document and updates everything together);
// bulkBusy: the running bulk saves, [{ scope, fileIds }].
const NO_BULK = [];
const noBulkSync = async () => null;

const AccountTable = ({
  account, onRerun, rerunState, tokens, domId, canEdit = false, onAnnotate, onLoadHistory, viewer,
  onBulkSync = noBulkSync, bulkBusy = NO_BULK,
}) => {
  const who = (by) => personName(by, viewer);
  const { current, older } = account;
  const { doc, fields, ref } = current;
  const annotations = doc.annotations || NO_ANNOTATIONS;
  const [showSummary, setShowSummary] = useState(false);
  const [showOlder, setShowOlder] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [draft, setDraft] = useState('');
  const [busyKey, setBusyKey] = useState(null);
  const [error, setError] = useState('');
  const [noteDraft, setNoteDraft] = useState(null);
  // The version (and extraction revision) each editor started from: a save
  // sends them, so a change someone else made meanwhile, or a re-run that
  // moved the accounts, is a conflict (409), never overwritten. After a
  // conflict the editor shows what changed; saving again then replaces it
  // deliberately.
  const [editingVersion, setEditingVersion] = useState(0);
  const [editingRevision, setEditingRevision] = useState(null);
  const [editConflict, setEditConflict] = useState(false);
  const [noteVersion, setNoteVersion] = useState(0);
  const [noteRevision, setNoteRevision] = useState(null);
  const [noteConflict, setNoteConflict] = useState(false);
  const [history, setHistory] = useState(null);

  const institution = institutionOf(fields);
  const title = titleOf(fields);
  const matchKey = matchKeyOf(fields);
  const asOf = asOfOf(fields);
  const promptName = doc.dataPointsDetails && doc.dataPointsDetails.prompt_name;
  // Which version of the FP Build prompt ran (recorded since extraction runs
  // the Build prompts directly), so an edited prompt is easy to tell apart.
  const promptVersion = doc.dataPointsDetails && doc.dataPointsDetails.prompt_version;
  const accountIndex = ref && ref.listKey !== null && ref.listKey !== undefined ? ref.index : null;

  const rows = fieldRowsOf(fields, editing);
  const editableEntries = valueEntriesOf(fields, ref, true);
  const lookup = {
    correction: (row) => rowAnnotations(annotations, ref, row).correction,
    mark: (row) => rowAnnotations(annotations, ref, row).mark,
  };
  const rowByKey = (rowKey) => editableEntries.find((row) => row.rowKey === rowKey);
  const syncable = valueEntriesOf(fields, ref);
  // Counted in items: a nested table's row counts once, however many values it has.
  const completionUnder = (prefix, itemOf = (entry) => entry.item) => completionOf(
    syncable.filter((entry) => startsWithPath(entry.path, prefix)), lookup.mark, itemOf,
  );
  const { done: syncedCount, total: syncTotal } = completionUnder([]);
  const accountComplete = syncTotal > 0 && syncedCount === syncTotal;
  // A bulk save covering this document: its counts and checks wait for it.
  const docBulkBusy = bulkBusy.some((b) => b.fileIds.includes(doc.fileId));
  const bulkScope = (scope) => `${account.key}|${scope}`;
  const syncUnder = async (scope, prefix, synced) => {
    setError('');
    const targets = syncable.filter((entry) => startsWithPath(entry.path, prefix)).map((entry) => ({ doc, entry }));
    const message = await onBulkSync(bulkScope(scope), targets, synced);
    if (message) setError(message);
  };
  const showSync = canEdit || annotations.sync.length > 0;
  const note = accountNote(annotations, ref);
  // Corrections and notes a re-run left without a place; shown once per document.
  const unplaced = accountIndex === null || accountIndex === 0
    ? [
      ...annotations.corrections.filter((c) => !c.path).map((c) => `${(c.field || []).map(fieldLabel).join(' › ')}: ${shown(c.value)}`),
      ...annotations.notes.filter((n) => n.orphaned).map((n) => `Note: ${n.text}`),
    ]
    : [];

  // The change shows at once (the report applies it before the server
  // confirms); a failure is taken back there and explained here.
  const run = async (rowKey, kind, payload, hint) => {
    setBusyKey(rowKey);
    setError('');
    const message = await onAnnotate(doc.fileId, kind, payload, hint);
    setBusyKey(null);
    if (message) setError(message);
    return !message;
  };

  const edit = {
    who,
    editing,
    canEdit,
    showSync,
    busyKey,
    docBulkBusy,
    isBulkBusy: (scope) => bulkBusy.some((b) => b.scope === bulkScope(scope)),
    completionUnder,
    syncUnder,
    editingKey,
    draft,
    setDraft,
    rowByKey,
    lookup,
    startEdit: (rowKey, value) => {
      const row = rowByKey(rowKey);
      const existing = row && lookup.correction(row);
      setEditingKey(rowKey);
      setEditingVersion(existing ? existing.version : 0);
      setEditingRevision(doc.extractionRevision);
      setEditConflict(false);
      setDraft(isEmpty(value) ? '' : String(value));
      setError('');
    },
    cancelEdit: () => setEditingKey(null),
    saveEdit: async () => {
      const rowKey = editingKey;
      const row = rowByKey(rowKey);
      if (!row) return;
      const current = lookup.correction(row);
      // After a conflict the user has seen the newer value: replace it.
      const version = editConflict ? (current ? current.version : 0) : editingVersion;
      const revision = editConflict ? doc.extractionRevision : editingRevision;
      const typed = draft;
      setEditingKey(null);
      // Saving the value that's already there changes nothing.
      if (typedMatches(typed, row.value)) return;
      // Typing the extracted value back undoes the correction.
      const undo = current && typedMatches(typed, current.original);
      const ok = await run(rowKey, 'correction', undo ? {
        path: row.valuePath,
        action: 'revert',
        expectedVersion: version,
        extractionRevision: revision,
      } : {
        path: row.valuePath,
        value: typed,
        expectedVersion: version,
        extractionRevision: revision,
      }, { original: row.value });
      if (!ok) {
        // Back to the editor with what was typed, so nothing is lost.
        setEditingKey(rowKey);
        setDraft(typed);
        setEditingVersion(version);
        setEditingRevision(revision);
        setEditConflict(true);
      }
    },
    revert: (rowKey) => {
      const row = rowByKey(rowKey);
      const existing = row && lookup.correction(row);
      if (!existing) return;
      run(rowKey, 'correction', {
        path: row.valuePath, action: 'revert', expectedVersion: existing.version, extractionRevision: doc.extractionRevision,
      });
    },
    toggleSync: (rowKey, value, synced) => {
      const row = rowByKey(rowKey);
      if (!row) return;
      const mark = lookup.mark(row);
      return run(rowKey, 'sync', {
        path: row.syncPath,
        synced: synced === undefined ? !(mark && !mark.stale) : synced,
        expectedValue: value === undefined ? null : value,
        extractionRevision: doc.extractionRevision,
      });
    },
    // One save for the whole account; nothing flips until it's done.
    markComplete: (complete) => syncUnder('account', [], complete),
    sharedNote: editingKey && rowByKey(editingKey) && rowByKey(editingKey).childKey === undefined
      && isSharedField(ref, rowByKey(editingKey).path[0])
      ? 'Shared by every account in this document' : '',
    conflictNote: (() => {
      const row = editConflict && editingKey && rowByKey(editingKey);
      if (!row) return '';
      if (doc.extractionRevision !== editingRevision) return RERUN_CONFLICT;
      const current = lookup.correction(row);
      if ((current ? current.version : 0) === editingVersion) return '';
      return current
        ? `Changed by ${who(current.by)} while you were editing: now ${shown(current.value)}. Save to replace it.`
        : 'Set back to the extracted value while you were editing. Save to replace it.';
    })(),
  };

  const openNote = () => {
    setNoteDraft(note ? note.text : '');
    setNoteVersion(note ? note.version : 0);
    setNoteRevision(doc.extractionRevision);
    setNoteConflict(false);
  };

  const saveNote = async () => {
    const text = noteDraft;
    const version = noteConflict ? (note ? note.version : 0) : noteVersion;
    const revision = noteConflict ? doc.extractionRevision : noteRevision;
    setNoteDraft(null);
    const ok = await run('note', 'note', { accountIndex, text, expectedVersion: version, extractionRevision: revision });
    if (!ok) {
      setNoteDraft(text);
      setNoteVersion(version);
      setNoteRevision(revision);
      setNoteConflict(true);
    }
  };

  const noteConflictText = noteConflict && doc.extractionRevision !== noteRevision
    ? RERUN_CONFLICT
    : noteConflict && (note ? note.version : 0) !== noteVersion
    ? (note
      ? `Changed by ${who(note.by)} while you were editing: “${note.text}”. Save to replace it.`
      : 'Removed by someone else while you were editing. Save to add it back.')
    : '';

  const toggleHistory = async () => {
    if (history) {
      setHistory(null);
      return;
    }
    setHistory({ loading: true, entries: [] });
    try {
      const data = await onLoadHistory(doc.fileId);
      setHistory({ loading: false, entries: (data && data.history) || [] });
    } catch (err) {
      setHistory({ loading: false, entries: [], error: 'Could not load the history.' });
    }
  };

  return (
    <div id={domId} className="client-report-account border rounded mb-3" data-testid={`account-${account.key}`}>
      <div className="d-flex flex-wrap align-items-baseline gap-2 px-2 py-1 bg-light border-bottom">
        <strong data-testid="account-title">
          <Highlight text={title || institution || doc.subTypeName || doc.docTypeName || 'Account'} tokens={tokens} />
        </strong>
        {title && institution && <span className="text-muted"><Highlight text={institution} tokens={tokens} /></span>}
        {matchKey && matchKey.length <= 6 && (
          <span className="text-muted">Account ••<Highlight text={matchKey} tokens={tokens} /></span>
        )}
        <span className="ms-auto d-flex flex-wrap align-items-baseline gap-2">
          {showSync && syncable.length > 0 && (docBulkBusy ? (
            <span className="small text-muted" role="status" data-testid="sync-saving">
              <span className="spinner-border spinner-border-sm me-1" aria-hidden="true"></span>Saving…
            </span>
          ) : (
            <span
              className={`small ${syncedCount === syncTotal ? 'text-success' : 'text-muted'}`}
              data-testid="sync-count"
              title="Values checked off as entered in eMoney"
            >
              {syncedCount}/{syncTotal} in eMoney
            </span>
          ))}
          {canEdit && syncable.length > 0 && (
            <label className="small d-print-none d-inline-flex align-items-center gap-1 mb-0">
              <input
                type="checkbox"
                className="form-check-input mt-0"
                aria-label="Mark account complete"
                checked={accountComplete}
                disabled={docBulkBusy}
                onChange={(event) => edit.markComplete(event.target.checked)}
              />
              Complete
            </label>
          )}
          {asOf && <span className="text-muted small">As of {String(asOf)}</span>}
          {canEdit && (
            <button
              type="button"
              className={`btn btn-sm py-0 d-print-none ${editing ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => { setEditing((v) => !v); setEditingKey(null); }}
              aria-pressed={editing}
            >
              <i className="bi bi-pencil-square me-1"></i>{editing ? 'Done editing' : 'Edit values'}
            </button>
          )}
        </span>
      </div>

      {error && <div className="alert alert-warning py-1 px-2 mb-0 rounded-0 small d-print-none" role="alert">{error}</div>}

      <table className="table table-sm table-bordered align-middle mb-0">
        <tbody>
          <FieldRows fields={fields} rows={rows} tokens={tokens} edit={edit} lookup={lookup} />
        </tbody>
      </table>

      {(note || noteDraft !== null) && (
        <div className="client-report-note border-top px-2 py-2 small" data-testid="account-note">
          <div className="fw-semibold mb-1"><i className="bi bi-sticky me-1" aria-hidden="true"></i>Note</div>
          {noteDraft !== null ? (
            <form className="d-print-none" onSubmit={(e) => { e.preventDefault(); saveNote(); }}>
              {noteConflictText && (
                <div className="text-warning-emphasis mb-1" data-testid="note-conflict">{noteConflictText}</div>
              )}
              <textarea
                className="form-control form-control-sm mb-1"
                rows={3}
                value={noteDraft}
                onChange={(e) => setNoteDraft(e.target.value)}
                aria-label="Note"
                maxLength={4000}
                autoFocus
              />
              <div className="d-flex gap-1">
                <button type="submit" className="btn btn-sm btn-primary" disabled={busyKey === 'note'}>Save note</button>
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setNoteDraft(null)} disabled={busyKey === 'note'}>Cancel</button>
              </div>
            </form>
          ) : (
            <>
              <div style={{ whiteSpace: 'pre-wrap' }} data-testid="account-note-text">{note.text}</div>
              <div className="text-muted mt-1" data-testid="account-note-by">
                {who(note.by)}
                {note.at && <> · {formatWhen(note.at)}</>}
                {note.version > 1 && ' · edited'}
                {note.pending && ' · saving…'}
              </div>
            </>
          )}
        </div>
      )}

      {unplaced.length > 0 && (
        <div className="border-top px-2 py-1 small text-warning-emphasis d-print-none" data-testid="account-unplaced">
          <i className="bi bi-exclamation-triangle me-1"></i>
          Kept from before a re-run, with no matching field now: {unplaced.join(' · ')}
        </div>
      )}

      <div className="px-2 py-1 small d-flex flex-wrap align-items-center gap-3 border-top">
        <span data-testid="account-source">
          <span className="text-muted">Source:</span> <Highlight text={doc.documentName || doc.fileId} tokens={tokens} />
        </span>
        <SourceLinks doc={doc} />
        {promptName && (
          <span className="text-muted d-print-none" data-testid="account-prompt">
            From <code>{promptName}</code>{promptVersion ? ` v${promptVersion}` : ''}
          </span>
        )}
        <RerunButton fileId={doc.fileId} state={rerunState} onRerun={onRerun} />
        {canEdit && noteDraft === null && (
          <button type="button" className="btn btn-link btn-sm p-0 d-print-none" onClick={openNote}>
            {note ? 'Edit note' : 'Add note'}
          </button>
        )}
        {doc.summary && (
          <button type="button" className="btn btn-link btn-sm p-0 d-print-none" onClick={() => setShowSummary((v) => !v)}>
            {showSummary ? 'Hide summary' : 'Summary'}
          </button>
        )}
        {onLoadHistory && (
          <button type="button" className="btn btn-link btn-sm p-0 d-print-none" onClick={toggleHistory}>
            {history ? 'Hide history' : 'History'}
          </button>
        )}
        {older.length > 0 && (
          <button type="button" className="btn btn-link btn-sm p-0 d-print-none" onClick={() => setShowOlder((v) => !v)}>
            {showOlder ? 'Hide older statements' : `Older statements (${older.length})`}
          </button>
        )}
      </div>

      {showSummary && doc.summary && (
        <div
          className="summary-content small border-top px-2 py-1 d-print-none"
          data-testid="account-summary"
          dangerouslySetInnerHTML={{ __html: textToHtml(doc.summary) }}
        />
      )}

      {history && <HistoryList state={history} who={who} />}

      {showOlder && (
        <ul className="list-unstyled small border-top px-2 py-1 mb-0 d-print-none" data-testid="older-statements">
          {older.map(({ doc: olderDoc, asOf: olderAsOf, time }) => (
            <li key={olderDoc.fileId} className="d-flex flex-wrap gap-2">
              <span>{olderDoc.documentName || olderDoc.fileId}</span>
              <span className="text-muted">{olderAsOf ? `as of ${olderAsOf}` : (time ? formatTimestamp(time) : '')}</span>
              <SourceLinks doc={olderDoc} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AccountTable;
